import { useState, useEffect } from 'react';

interface PollOption {
  id: string;
  label: string;
  emoji: string;
  votes: number;
}

const POLL_STORAGE_KEY = 'meow_poll_vote';
const POLL_VOTES_KEY = 'meow_poll_votes';

const initialOptions: PollOption[] = [
  { id: 'purring', label: 'Purring/Sleeping', emoji: '😴', votes: 1247 },
  { id: 'cuddles', label: 'Cuddles & Affection', emoji: '🤗', votes: 1893 },
  { id: 'playfulness', label: 'Playfulness & Zoomies', emoji: '🎮', votes: 1654 },
  { id: 'funny', label: 'Funny Moments', emoji: '😂', votes: 2108 },
  { id: 'eyes', label: 'Their Beautiful Eyes', emoji: '👁️', votes: 1432 },
  { id: 'personality', label: 'Their Personality', emoji: '🐾', votes: 1789 },
];

export const CatPollSection = () => {
  const [hasVoted, setHasVoted] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [options, setOptions] = useState<PollOption[]>(initialOptions);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Load existing vote
    const existingVote = localStorage.getItem(POLL_STORAGE_KEY);
    if (existingVote) {
      setHasVoted(true);
      setSelectedOption(existingVote);
    }

    // Load vote counts
    const savedVotes = localStorage.getItem(POLL_VOTES_KEY);
    if (savedVotes) {
      try {
        const parsedVotes = JSON.parse(savedVotes);
        setOptions(parsedVotes);
      } catch (e) {
        // Use initial options if parsing fails
        localStorage.setItem(POLL_VOTES_KEY, JSON.stringify(initialOptions));
      }
    } else {
      localStorage.setItem(POLL_VOTES_KEY, JSON.stringify(initialOptions));
    }

    // Trigger animation after mount
    setTimeout(() => setIsAnimating(true), 100);
  }, []);

  const handleVote = (optionId: string) => {
    if (hasVoted) return;

    // Update vote counts
    const updatedOptions = options.map(opt =>
      opt.id === optionId ? { ...opt, votes: opt.votes + 1 } : opt
    );

    setOptions(updatedOptions);
    setSelectedOption(optionId);
    setHasVoted(true);

    // Save to localStorage
    localStorage.setItem(POLL_STORAGE_KEY, optionId);
    localStorage.setItem(POLL_VOTES_KEY, JSON.stringify(updatedOptions));
  };

  const totalVotes = options.reduce((sum, opt) => sum + opt.votes, 0);

  const getPercentage = (votes: number) => {
    return totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
  };

  const maxVotes = Math.max(...options.map(opt => opt.votes));

  return (
    <section className="py-10 px-4 bg-gradient-to-b from-white to-teal-50">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md p-6 md:p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
              What's your favorite thing about your cat? 🐱
            </h2>
            <p className="text-gray-500 text-sm">
              {hasVoted ? 'Thanks for voting! Here are the results:' : 'Cast your vote and see what other cat lovers think!'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {options.map((option) => {
              const percentage = getPercentage(option.votes);
              const isWinning = option.votes === maxVotes && totalVotes > 0;
              const isSelected = selectedOption === option.id;

              return (
                <div key={option.id} className="relative">
                  <button
                    onClick={() => handleVote(option.id)}
                    disabled={hasVoted}
                    className={`w-full text-left relative overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                      hasVoted
                        ? 'cursor-default'
                        : 'cursor-pointer hover:border-teal-400 hover:shadow-md'
                    } ${
                      isSelected
                        ? 'border-teal-500'
                        : isWinning && hasVoted
                        ? 'border-teal-400'
                        : 'border-gray-200'
                    }`}
                  >
                    {/* Animated background bar */}
                    <div
                      className={`absolute inset-0 transition-all duration-1000 ease-out ${
                        isWinning && hasVoted ? 'bg-teal-100' : 'bg-gray-50'
                      }`}
                      style={{
                        width: hasVoted && isAnimating ? `${percentage}%` : '0%',
                      }}
                    />

                    {/* Content */}
                    <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{option.emoji}</span>
                        <span className="text-sm font-semibold text-gray-800 leading-tight">
                          {option.label}
                        </span>
                      </div>

                      {hasVoted && (
                        <span
                          className={`text-sm font-bold ml-2 whitespace-nowrap ${
                            isWinning ? 'text-teal-600' : 'text-gray-500'
                          }`}
                        >
                          {percentage}%
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>

          {hasVoted && (
            <div className="mt-4 text-center">
              <p className="text-gray-400 text-xs">
                Total votes: <span className="font-semibold text-gray-600">{totalVotes.toLocaleString()}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
